/* eslint-disable import/no-cycle */
import { enqueueSnackbar } from 'notistack';
import { includes } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { createAction } from 'redux-actions';

import { resetForm } from '@gef-ui/form-core/stream/transfer/control';
import { getMessage } from '@gef-ui/localization/messages';

import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import { saveFile } from 'features/common/fileSave/actions';
import typeTasks from 'features/other/typeTasks';
import { setTaskView } from 'features/other/typeTasks/actions';
import taskSearch from 'features/views/taskSearch';
import user from 'features/views/user';
import contextUser from 'features/views/userContext';
import { history } from 'utils';

import { ADD_TASK_NOTE, RESET_REDUCER, SET_HISTORY, SET_TASK_ATTACHMENTS, SET_TASK_DETAILS } from './actionTypes';
import { dialogs, TAKE_TASK_ALREADY_PROCESSED_ERR_MSG, TAKE_TASK_USER_HAS_NO_ACCESS_ERR_MSG } from './constants';
import { FIELDS, TASK_DETAIL_FORM } from './form';

const setTaskDetails = createAction(SET_TASK_DETAILS, (details) => ({ details }));
const setHistory = createAction(SET_HISTORY, (history) => ({ history }));
const setTaskAttachments = createAction(SET_TASK_ATTACHMENTS, (attachments, type) => ({ attachments, type }));

export const resetReducer = createAction(RESET_REDUCER);

export const addNote = createAction(ADD_TASK_NOTE, (comment, firstName, surname) => ({ comment, firstName, surname }));

export const fetchDetails = (taskId) => (dispatch) => {
	dispatch(
		fetch.actions.get(fetch.constants.urls.taskDetail, {
			replaceParams: { id: taskId },
			onSuccess: [
				(response) => () => {
					dispatch(setTaskDetails(response.body));
					dispatch(fetchAttachments(taskId));
					dispatch(fetchHistory(taskId));
				},
			],
			shouldReportError: ({ failure }) => {
				return failure?.status !== 403;
			},
		}),
	);
};

export const fetchAttachments = (taskId) => (dispatch) => {
	dispatch(
		fetch.actions.get(fetch.constants.urls.taskAttachment, {
			replaceParams: { id: taskId },
			onSuccess: [
				(response) => () => {
					dispatch(setTaskAttachments(response.body));
				},
			],
		}),
	);
};

export const fetchHistory = (taskId) => (dispatch) => {
	dispatch(
		fetch.actions.get(fetch.constants.urls.taskHistory, {
			replaceParams: { id: taskId },
		}),
	).then((response) => {
		dispatch(setHistory(response.body));
	});
};

export const postNote =
	(taskId) =>
	async (dispatch, getState, { getFormFieldValue, sendControlNext }) => {
		const state = getState();

		const note = await getFormFieldValue({
			formName: TASK_DETAIL_FORM,
			fieldName: FIELDS.note,
		});

		const firstName = user.selectors.getFirstName(state);
		const surname = user.selectors.getSurname(state);

		return dispatch(
			fetch.actions.post(fetch.constants.urls.taskNote, {
				replaceParams: { id: taskId },
				body: { note },
				onSuccess: [
					() => () => {
						dispatch(addNote(note, firstName, surname));
						enqueueSnackbar(getMessage('toastr:approveTextHeader'), { variant: 'success' });
						sendControlNext(TASK_DETAIL_FORM, resetForm());
					},
				],
				onError: [() => enqueueSnackbar(getMessage('toastr:errorTextHeader'), { variant: 'error' })],
			}),
		);
	};

export const postTakeTask = (taskId, taskTypeId) => (dispatch) => {
	dispatch(
		fetch.actions.post(fetch.constants.urls.taskTake, {
			replaceParams: { id: taskId },
			onSuccess: [
				() => () => {
					dispatch(fetchDetails(taskId));
					dispatch(fetchAttachments(taskId));
					dispatch(typeTasks.actions.reloadCurrentPage(taskTypeId));
					dispatch(taskSearch.actions.paginationClick(0));
					enqueueSnackbar(getMessage('toastr:approveTakeMessage'), { variant: 'success' });
				},
			],
			onError: [
				({ failure }) =>
					() => {
						if (failure?.parsedContent?.status === 403) {
							if (includes(TAKE_TASK_USER_HAS_NO_ACCESS_ERR_MSG, failure?.parsedContent?.message)) {
								enqueueSnackbar(getMessage('toastr:takeTaskErrorUserHasNoAccess'), {
									variant: 'error',
								});

								history.go(-1);
							}

							if (failure?.parsedContent?.message === TAKE_TASK_ALREADY_PROCESSED_ERR_MSG) {
								enqueueSnackbar(getMessage('toastr:takeTaskErrorAlreadyProcessed'), {
									variant: 'error',
								});
								dispatch(fetchDetails(taskId));
								dispatch(fetchAttachments(taskId));
							}
						}
					},
			],
			shouldReportError: ({ failure }) => {
				return failure?.status !== 403;
			},
		}),
	);
};

export const setViewedState = (taskId) => (dispatch, getState) => {
	const user = contextUser.selectors.getContextUser(getState());
	dispatch(
		fetch.actions.post(fetch.constants.urls.taskOpen, {
			replaceParams: { id: taskId },
			uniqueReqID: taskId,
			onSuccess: [
				// eslint-disable-next-line
				(response) => () => {
					if (isNilOrEmpty(user)) {
						dispatch(setTaskView(taskId, true));
					}
					dispatch(fetchDetails(taskId));
					dispatch(fetchAttachments(taskId));
				},
			],
			//onError logic - in TaskDetail useEffect because of "navigate"
			shouldReportError: ({ failure }) => {
				return failure?.status !== 403;
			},
		}),
	);
};

export const unsetTaskViewed = (taskId) => (dispatch) =>
	dispatch(
		fetch.actions.put(fetch.constants.urls.taskUnread, {
			replaceParams: { id: taskId },
		}),
	);

export const downloadAttachment = (taskId, attachmentId) => async (dispatch) => {
	await dispatch(
		fetch.actions.getBinary(fetch.constants.urls.taskAttachmentById, {
			uniqueReqID: attachmentId,
			replaceParams: {
				id: taskId,
				attachmentId,
			},
			onSuccess: [
				(response) => () => {
					saveFile({ response });
				},
			],
			onError: [
				(response) => () => {
					if (!response || !response.failure) return;
					const status = response.failure.status;

					if (status === 404) {
						dispatch(dialog.actions.open(dialogs.errorAttachmentNotFound));
					}
				},
			],
			shouldReportError: ({ failure }) => {
				return includes(failure.status, [400, 403]);
			},
		}),
	);
};
